import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Avatar,
  IconButton,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  ArrowBack,
  Coffee,
  LocationOn,
  Star,
  AttachMoney,
  FilterList,
  Refresh,
  Favorite
} from '@mui/icons-material';
import axios from 'axios';

// Fix for default markers
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface Cafe {
  id: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  priceLevel?: number;
  isPartnerCafe?: boolean;
  allowsAnonymousMatching?: boolean;
  type?: string;
  source?: string;
  placeId?: string;
  addedBy?: string;
  photoURLs?: string[];
  features?: string[];
}

// Cafe chain types
const CAFE_CHAINS = [
  'Starbucks',
  'Mikel',
  'Gloria Jeans',
  'Nevada',
  'Coffy',
  'Espresso Lab',
  'Kahve Dünyası'
] as const;

const CafeMapPage: React.FC = () => {
  const navigate = useNavigate();
  const [cafes, setCafes] = useState<Cafe[]>([]);
  const [filteredCafes, setFilteredCafes] = useState<Cafe[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterSource, setFilterSource] = useState<string>('all');

  // Turkey center coordinates
  const defaultCenter: [number, number] = [39.9334, 32.8597];
  const defaultZoom = 6;

  const fetchCafes = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Fetch ALL cafes from Backend API (both 'cafes' and 'partnerCafes' collections)
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('https://api.sobcof.com/api/partner-cafes', {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { includeAll: 'true' }
      });

      if (response.data.success) {
        const cafesData = response.data.data.filter((cafe: any) =>
          cafe.latitude && cafe.longitude
        );
        setCafes(cafesData);
        setFilteredCafes(cafesData);
        console.log(`✅ Loaded ${cafesData.length} cafes from Backend API`);
      } else {
        setError('Kafeler alınamadı');
      }
    } catch (error: any) {
      console.error('Error fetching cafes:', error);
      setError('Kafeler yüklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCafes();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...cafes];

    // Filter by type
    if (filterType !== 'all') {
      if (filterType === 'partner') {
        filtered = filtered.filter(cafe => cafe.isPartnerCafe === true);
      } else if (CAFE_CHAINS.includes(filterType as any)) {
        filtered = filtered.filter(cafe => cafe.type === filterType);
      } else if (filterType === 'chains') {
        filtered = filtered.filter(cafe => CAFE_CHAINS.includes(cafe.type as any));
      } else if (filterType === 'other') {
        filtered = filtered.filter(cafe =>
          !CAFE_CHAINS.includes(cafe.type as any) && !cafe.isPartnerCafe
        );
      }
    }

    // Filter by source
    if (filterSource !== 'all') {
      filtered = filtered.filter(cafe => cafe.source === filterSource);
    }

    setFilteredCafes(filtered);
  }, [filterType, filterSource, cafes]);

  const getCafeIcon = (cafe: Cafe) => {
    let color = '#D2691E'; // Default: orange

    if (cafe.isPartnerCafe) {
      color = '#8B4513'; // Partner: dark brown
    } else if (cafe.type === 'Starbucks') {
      color = '#00704A'; // Starbucks: green
    } else if (cafe.type === 'Mikel') {
      color = '#E31837'; // Mikel: red
    } else if (cafe.type === 'Gloria Jeans') {
      color = '#6B4423'; // Gloria Jeans: brown
    } else if (cafe.type === 'Nevada') {
      color = '#FF6B35'; // Nevada: orange-red
    } else if (cafe.type === 'Coffy') {
      color = '#4A3728'; // Coffy: dark brown
    } else if (cafe.type === 'Espresso Lab') {
      color = '#2C1810'; // Espresso Lab: very dark brown
    } else if (cafe.type === 'Kahve Dünyası') {
      color = '#C41E3A'; // Kahve Dünyası: crimson
    }

    return new Icon({
      iconUrl: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
        <svg width="35" height="35" viewBox="0 0 35 35" xmlns="http://www.w3.org/2000/svg">
          <circle cx="17.5" cy="17.5" r="16" fill="${color}" stroke="white" stroke-width="2.5"/>
          <text x="17.5" y="23" text-anchor="middle" fill="white" font-size="18" font-weight="bold">☕</text>
        </svg>
      `)}`,
      iconSize: [35, 35],
      iconAnchor: [17.5, 17.5],
      popupAnchor: [0, -17.5]
    });
  };

  const getPriceLevelText = (priceLevel?: number) => {
    if (!priceLevel) return 'Belirtilmemiş';
    return '$'.repeat(priceLevel);
  };

  const partnerCafes = cafes.filter(c => c.isPartnerCafe).length;
  const chainCafes = cafes.filter(c => CAFE_CHAINS.includes(c.type as any)).length;
  const starbucksCafes = cafes.filter(c => c.type === 'Starbucks').length;
  const mikelCafes = cafes.filter(c => c.type === 'Mikel').length;
  const gloriaJeansCafes = cafes.filter(c => c.type === 'Gloria Jeans').length;
  const nevadaCafes = cafes.filter(c => c.type === 'Nevada').length;
  const coffyCafes = cafes.filter(c => c.type === 'Coffy').length;
  const espressoLabCafes = cafes.filter(c => c.type === 'Espresso Lab').length;
  const kahveDunyasiCafes = cafes.filter(c => c.type === 'Kahve Dünyası').length;
  const otherCafes = cafes.filter(c => !CAFE_CHAINS.includes(c.type as any) && !c.isPartnerCafe).length;

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f8fafc' }}>
      {/* App Bar */}
      <AppBar
        position="static"
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
          boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Avatar sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            mr: 2,
            width: 40,
            height: 40
          }}>
            <Coffee sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h5" component="div" sx={{
            flexGrow: 1,
            fontWeight: 600,
            color: 'white'
          }}>
            Kafeler Haritası
          </Typography>
          <IconButton color="inherit" onClick={fetchCafes} size="large" title="Yenile">
            <Refresh />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Header with Stats and Filters */}
      <Paper sx={{ p: 3, m: 2, borderRadius: 3 }}>
        {/* Summary Statistics */}
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
          justifyContent: 'center'
        }}>
          <Box sx={{ flex: '1 1 200px', minWidth: '180px', maxWidth: '250px' }}>
            <Card sx={{
              textAlign: 'center',
              background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
              color: 'white',
              borderRadius: 3,
              height: '100%'
            }}>
              <CardContent sx={{ py: 2 }}>
                <Coffee sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {cafes.length}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Toplam Kafe
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 200px', minWidth: '180px', maxWidth: '250px' }}>
            <Card sx={{
              textAlign: 'center',
              background: 'linear-gradient(135deg, #D2691E 0%, #CD853F 100%)',
              color: 'white',
              borderRadius: 3,
              height: '100%'
            }}>
              <CardContent sx={{ py: 2 }}>
                <Star sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {partnerCafes}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Partner Kafe
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 200px', minWidth: '180px', maxWidth: '250px' }}>
            <Card sx={{
              textAlign: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 3,
              height: '100%'
            }}>
              <CardContent sx={{ py: 2 }}>
                <Coffee sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {chainCafes}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Zincir Kafeler
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 200px', minWidth: '180px', maxWidth: '250px' }}>
            <Card sx={{
              textAlign: 'center',
              background: 'linear-gradient(135deg, #CD853F 0%, #DEB887 100%)',
              color: 'white',
              borderRadius: 3,
              height: '100%'
            }}>
              <CardContent sx={{ py: 2 }}>
                <LocationOn sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {otherCafes}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Diğer Kafeler
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FilterList color="primary" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mr: 2 }}>
            Filtreler:
          </Typography>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Kafe Tipi</InputLabel>
            <Select
              value={filterType}
              label="Kafe Tipi"
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="all">Tümü ({cafes.length})</MenuItem>
              <MenuItem value="partner">Partner Kafeler ({partnerCafes})</MenuItem>
              <MenuItem value="chains">Tüm Zincirler ({chainCafes})</MenuItem>
              <Divider />
              <MenuItem value="Starbucks">Starbucks ({starbucksCafes})</MenuItem>
              <MenuItem value="Mikel">Mikel ({mikelCafes})</MenuItem>
              <MenuItem value="Gloria Jeans">Gloria Jeans ({gloriaJeansCafes})</MenuItem>
              <MenuItem value="Nevada">Nevada ({nevadaCafes})</MenuItem>
              <MenuItem value="Coffy">Coffy ({coffyCafes})</MenuItem>
              <MenuItem value="Espresso Lab">Espresso Lab ({espressoLabCafes})</MenuItem>
              <MenuItem value="Kahve Dünyası">Kahve Dünyası ({kahveDunyasiCafes})</MenuItem>
              <Divider />
              <MenuItem value="other">Diğer ({otherCafes})</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Kaynak</InputLabel>
            <Select
              value={filterSource}
              label="Kaynak"
              onChange={(e) => setFilterSource(e.target.value)}
            >
              <MenuItem value="all">Tümü</MenuItem>
              <MenuItem value="partner">Partner</MenuItem>
              <MenuItem value="google_places">Google Places</MenuItem>
              <MenuItem value="system">Sistem</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>

          <Chip
            label={`${filteredCafes.length} kafe gösteriliyor`}
            color="primary"
            icon={<Coffee />}
            sx={{ ml: 'auto', fontWeight: 600 }}
          />
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ m: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Map Container */}
      <Box sx={{ flex: 1, position: 'relative', m: 2, borderRadius: 3, overflow: 'hidden', boxShadow: 3 }}>
        {isLoading ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f8fafc'
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={60} sx={{ color: '#8B4513', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Kafeler yükleniyor...
              </Typography>
            </Box>
          </Box>
        ) : (
          <MapContainer
            center={defaultCenter}
            zoom={defaultZoom}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />

            {/* Cafe Markers */}
            {filteredCafes.map((cafe) => (
              <Marker
                key={cafe.id}
                position={[cafe.latitude, cafe.longitude]}
                icon={getCafeIcon(cafe)}
              >
                <Popup maxWidth={300}>
                  <Box sx={{ minWidth: 250, p: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Avatar sx={{
                        bgcolor: cafe.isPartnerCafe ? 'primary.main' : 'secondary.main',
                        width: 40,
                        height: 40
                      }}>
                        <Coffee />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.2 }}>
                          {cafe.name}
                        </Typography>
                        {cafe.isPartnerCafe && (
                          <Chip
                            label="Partner Kafe"
                            size="small"
                            color="primary"
                            sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Adres:
                          </Typography>
                        </Box>
                        <Typography variant="body2">
                          {cafe.address || 'Belirtilmemiş'}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          icon={<AttachMoney sx={{ fontSize: 14 }} />}
                          label={getPriceLevelText(cafe.priceLevel)}
                          size="small"
                          variant="outlined"
                        />

                        {cafe.type && (
                          <Chip
                            label={cafe.type}
                            size="small"
                            sx={{
                              bgcolor: CAFE_CHAINS.includes(cafe.type as any)
                                ? (cafe.type === 'Starbucks' ? '#00704A' :
                                   cafe.type === 'Mikel' ? '#E31837' :
                                   cafe.type === 'Gloria Jeans' ? '#6B4423' :
                                   cafe.type === 'Nevada' ? '#FF6B35' :
                                   cafe.type === 'Coffy' ? '#4A3728' :
                                   cafe.type === 'Espresso Lab' ? '#2C1810' :
                                   cafe.type === 'Kahve Dünyası' ? '#C41E3A' : '#666')
                                : '#666',
                              color: 'white',
                              fontWeight: 600
                            }}
                          />
                        )}

                        {cafe.allowsAnonymousMatching && (
                          <Chip
                            icon={<Favorite sx={{ fontSize: 14 }} />}
                            label="Anonim Eşleşme"
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        )}
                      </Box>

                      {cafe.source && (
                        <Typography variant="caption" color="text.secondary">
                          Kaynak: <strong>{cafe.source}</strong>
                        </Typography>
                      )}

                      {cafe.addedBy && (
                        <Typography variant="caption" color="text.secondary">
                          Ekleyen: <strong>{cafe.addedBy}</strong>
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
      </Box>
    </Box>
  );
};

export default CafeMapPage;
